import { useMemo } from "react";
import type { Agent, Player, Round, RoundDetail } from "../api/types";
import { AbilityLookup, EFFECT_STYLE } from "../lib/abilities";
import { sideColor } from "../lib/view";
import { useCoarseTime, usePlayback } from "../player/store";

interface Props {
  players: Player[];
  agents: Map<string, Agent>;
  lookup: AbilityLookup;
  round: Round;
  detail: RoundDetail;
}

/** One line of what happened to, or because of, the selected player. */
interface Entry {
  timeMs: number;
  /** What the line is about, which decides its colour. */
  tone: "hurt" | "dealt" | "state" | "cast" | "gear";
  text: string;
  /** The other player involved, when there is one. */
  other?: Player;
}

/**
 * Everything that happened to one player in this round, in order: the damage
 * they took and who dealt it, the damage they dealt, the states they were put
 * in, the abilities they cast and what they picked up.
 *
 * It exists because a map shows where things happened but never what they cost.
 */
export function PlayerEvents({ players, agents, lookup, round, detail }: Props) {
  const timeMs = useCoarseTime(200);
  const focusPawn = usePlayback((s) => s.focusPawn);
  const player = players.find((p) => p.pawn === focusPawn);

  const entries = useMemo(
    () => (player ? build(player, players, lookup, detail) : []),
    [player, players, lookup, detail],
  );

  if (!player) {
    return (
      <p className="px-3 py-3 text-[11px] opacity-40">
        Sélectionnez un joueur pour voir ce qui lui est arrivé.
      </p>
    );
  }

  const agent = agents.get(player.codename);
  const happened = entries.filter((e) => e.timeMs <= timeMs);

  return (
    <div className="flex min-h-0 flex-col">
      <h3 className="flex items-center gap-2 px-3 pb-1 pt-3 text-[11px] uppercase tracking-wide opacity-50">
        Détail · {agent?.displayName ?? player.codename}
      </h3>
      <ol className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
        {happened.length === 0 && (
          <li className="py-2 text-[11px] opacity-40">Rien pour l'instant.</li>
        )}
        {happened
          .slice()
          .reverse()
          .map((entry, i) => (
            <li key={i} className="flex gap-2 py-[3px] text-[11px] leading-4">
              <span className="w-8 shrink-0 font-mono opacity-40">
                {clock(entry.timeMs - round.startMs)}
              </span>
              <span className="min-w-0 flex-1" style={{ color: TONE[entry.tone] }}>
                {entry.text}
                {entry.other && (
                  <span
                    className="ml-1 opacity-80"
                    style={{ color: sideColor(entry.other, round) }}
                  >
                    {agents.get(entry.other.codename)?.displayName ?? entry.other.codename}
                  </span>
                )}
              </span>
            </li>
          ))}
      </ol>
    </div>
  );
}

const TONE: Record<Entry["tone"], string> = {
  hurt: "#ff8a8a",
  dealt: "#a8e6a0",
  state: "#c9b6ff",
  cast: "#9fd8ff",
  gear: "#cfd3da",
};

function build(
  player: Player,
  players: Player[],
  lookup: AbilityLookup,
  detail: RoundDetail,
): Entry[] {
  const byPawn = new Map(players.map((p) => [p.pawn, p]));
  const out: Entry[] = [];

  for (const event of detail.events) {
    const amount = event.extra?.amount;
    const weapon = event.extra?.weapon;
    const bone = event.extra?.bone;
    // Only damage is read: every kill has a lethal damage event behind it, and
    // that one carries the amount and where it landed.
    if (event.kind === "damage") {
      const killed = !!event.extra?.killed;
      if (event.target === player.pawn) {
        out.push({
          timeMs: event.timeMs,
          tone: "hurt",
          text: `${killed ? "tué" : "touché"}${amount ? ` −${Math.round(amount)}` : ""}${
            weapon ? ` · ${weapon}` : ""
          }${bone ? ` · ${bone}` : ""} par`,
          other: event.actor !== undefined ? byPawn.get(event.actor) : undefined,
        });
      } else if (event.actor === player.pawn) {
        out.push({
          timeMs: event.timeMs,
          tone: "dealt",
          text: `${killed ? "élimine" : "touche"}${amount ? ` −${Math.round(amount)}` : ""}${
            weapon ? ` · ${weapon}` : ""
          }`,
          other: event.target !== undefined ? byPawn.get(event.target) : undefined,
        });
      }
      continue;
    }
    if (event.actor !== player.pawn) continue;
    if (event.kind === "plant") out.push({ timeMs: event.timeMs, tone: "gear", text: "pose le spike" });
    if (event.kind === "defuse") out.push({ timeMs: event.timeMs, tone: "gear", text: "désamorce" });
  }

  for (const effect of detail.effects) {
    if (effect.pawn !== player.pawn) continue;
    const style = EFFECT_STYLE[effect.category];
    if (effect.category === "ultimate") {
      const name = lookup.ability(player.codename, "Ultimate")?.displayName;
      out.push({ timeMs: effect.startMs, tone: "cast", text: `ultime : ${name ?? "actif"}` });
      continue;
    }
    const source = effect.source ? lookup.agent(effect.source)?.displayName : undefined;
    out.push({
      timeMs: effect.startMs,
      tone: "state",
      text: `${style?.label ?? effect.category}${source ? ` · ${source}` : ""}`,
    });
  }

  for (const u of detail.utility) {
    if (u.owner !== player.pawn) continue;
    const name = lookup.describe(u).name;
    out.push({ timeMs: u.spawnedMs, tone: "cast", text: `utilise ${name}` });
  }

  for (const change of detail.armament) {
    if (change.pawn !== player.pawn || change.kind !== "armour" || !change.label) continue;
    out.push({
      timeMs: change.timeMs,
      tone: "gear",
      text: `armure ${change.label.toLowerCase()} (+${change.amount})`,
    });
  }

  out.sort((a, b) => a.timeMs - b.timeMs);
  return out;
}

function clock(ms: number): string {
  // Floored, so the printed time never disagrees with the order.
  const total = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}
